import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ShoppingCart, Eye, DollarSign, Clock, CheckCircle2, Package,
  Search, RefreshCw, AlertCircle, X, MapPin, Phone, Mail,
  Truck, Box, User, Tag, MessageSquare, Bot, Check, Ban,
  CreditCard, FileText, Plus, Trash2,
} from 'lucide-react';
import orderService from '../../api/services/orderService';
import productService from '../../api/services/productService';
import invoiceService from '../../api/services/invoiceService';
import FactureModal from '../Factures/FactureModal';
import { loadLocalBranding } from '../Factures/branding';
import { useOrdersRealtime } from '../../contexts/OrdersRealtimeContext';
import { useToast } from '../../contexts/ToastContext';

// ─── Configuration des statuts ────────────────────────────────────────────────
const STATUS_CONFIG = {
  pending:    { label: 'En attente', cls: 'bg-yellow-500/20 text-yellow-500 border border-yellow-500/30' },
  confirmed:  { label: 'Confirmée',  cls: 'bg-blue-500/20 text-blue-500 border border-blue-500/30' },
  processing: { label: 'En cours',   cls: 'bg-purple-500/20 text-purple-500 border border-purple-500/30' },
  shipped:    { label: 'Expédiée',   cls: 'bg-orange-500/20 text-orange-500 border border-orange-500/30' },
  delivered:  { label: 'Livrée',     cls: 'bg-green-500/20 text-green-500 border border-green-500/30' },
  cancelled:  { label: 'Annulée',    cls: 'bg-red-500/20 text-red-500 border border-red-500/30' },
  refunded:   { label: 'Remboursée', cls: 'bg-gray-500/20 text-gray-400 border border-gray-500/30' },
};

const PAYMENT_CONFIG = {
  pending:  { label: 'Paiement en attente', cls: 'bg-yellow-500/20 text-yellow-500' },
  paid:     { label: 'Payée',               cls: 'bg-green-500/20 text-green-500' },
  failed:   { label: 'Paiement échoué',     cls: 'bg-red-500/20 text-red-500' },
  refunded: { label: 'Remboursée',          cls: 'bg-gray-500/20 text-gray-400' },
};

const FILTERS = [
  { id: 'all', label: 'Toutes' },
  { id: 'pending', label: 'En attente' },
  { id: 'confirmed', label: 'Confirmées' },
  { id: 'processing', label: 'En cours' },
  { id: 'shipped', label: 'Expédiées' },
  { id: 'delivered', label: 'Livrées' },
  { id: 'cancelled', label: 'Annulées' },
];

const DATE_FILTERS = [
  { id: 'all', label: 'Toutes dates' },
  { id: 'today', label: "Aujourd'hui" },
  { id: '7d', label: '7 derniers jours' },
  { id: '30d', label: '30 derniers jours' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Masque les placeholders des commandes WhatsApp (adresse 'N/A', email *@whatsapp.local)
const cleanValue = (v) => {
  if (v == null) return '';
  const s = String(v).trim();
  if (!s || s.toUpperCase() === 'N/A') return '';
  if (s.toLowerCase().endsWith('@whatsapp.local')) return '';
  return s;
};

const formatMoney = (amount, currency = 'EUR') => {
  const n = Number(amount) || 0;
  try {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
};

const formatDate = (date, withTime = false) => {
  if (!date) return '—';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '—';
  const opts = { day: 'numeric', month: 'long', year: 'numeric' };
  if (withTime) { opts.hour = '2-digit'; opts.minute = '2-digit'; }
  return d.toLocaleDateString('fr-FR', opts);
};

const isWhatsAppAI = (order) => order?.metadata?.source === 'whatsapp_ai';

const getItems = (order) => (Array.isArray(order?.items) ? order.items : []);

// ─── Composant ────────────────────────────────────────────────────────────────

export default function CommandesConnected() {
  const navigate = useNavigate();
  const toast = useToast();
  const { markSeen } = useOrdersRealtime();
  const qc = useQueryClient();

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Génération de facture depuis une commande (brouillon ouvert dans FactureModal)
  const [factureDraft, setFactureDraft] = useState(null);
  const [showFactureModal, setShowFactureModal] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);

  // Création manuelle d'une commande
  const [showCreateModal, setShowCreateModal] = useState(false);

  // ── Requête React Query ─────────────────────────────────────────────────────
  const { data: ordersData, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['orders'],
    queryFn: () => orderService.getOrders({ limit: 1000 }),
  });

  const orders = (ordersData?.success && Array.isArray(ordersData.data)) ? ordersData.data : [];

  // Remet à zéro le badge rouge de la sidebar dès qu'on ouvre la page + marque les commandes live comme vues
  useEffect(() => {
    markSeen();
  }, [markSeen]);

  // Les nouvelles commandes socket arrivent déjà dans le cache via OrdersRealtimeContext
  const mergedOrders = useMemo(() => {
    return [...orders].sort(
      (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
    );
  }, [orders]);

  // ── Filtres (statut + date + recherche) appliqués côté client ───────────────
  const filteredOrders = useMemo(() => {
    const now = Date.now();
    const DAY = 24 * 60 * 60 * 1000;

    return mergedOrders.filter((order) => {
      // Statut (s'applique aussi aux commandes temps réel)
      if (filterStatus !== 'all' && order.status !== filterStatus) return false;

      // Date
      if (dateFilter !== 'all') {
        const created = new Date(order.createdAt).getTime();
        if (isNaN(created)) return false;
        if (dateFilter === 'today') {
          if (new Date(order.createdAt).toDateString() !== new Date().toDateString()) return false;
        } else if (dateFilter === '7d' && now - created > 7 * DAY) return false;
        else if (dateFilter === '30d' && now - created > 30 * DAY) return false;
      }

      // Recherche (N° commande ou nom client)
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matches =
          order.orderNumber?.toLowerCase().includes(q) ||
          order.clientName?.toLowerCase().includes(q) ||
          order.clientPhone?.toLowerCase().includes(q);
        if (!matches) return false;
      }
      return true;
    });
  }, [mergedOrders, filterStatus, dateFilter, searchQuery]);

  // ── Métriques ───────────────────────────────────────────────────────────────
  const totalOrders = mergedOrders.length;
  const primaryCurrency = mergedOrders[0]?.currency || 'EUR';
  const totalRevenue = mergedOrders.reduce((sum, o) => sum + (Number(o.total) || 0), 0);
  const deliveredOrders = mergedOrders.filter((o) => o.status === 'delivered').length;
  const pendingOrders = mergedOrders.filter((o) => o.status === 'pending').length;
  const completionRate = totalOrders > 0 ? Math.round((deliveredOrders / totalOrders) * 100) : 0;

  // ── Détail ──────────────────────────────────────────────────────────────────
  const handleViewOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
  };

  const handleCloseOrderDetails = () => {
    setShowOrderDetails(false);
    setSelectedOrder(null);
  };

  // ── Actions sur le statut ───────────────────────────────────────────────────
  const applyLocalUpdate = useCallback((updated) => {
    qc.setQueryData(['orders'], (prev) => {
      if (!prev?.data) return prev;
      const exists = prev.data.some((o) => o._id === updated._id);
      return {
        ...prev,
        data: exists
          ? prev.data.map((o) => (o._id === updated._id ? { ...o, ...updated } : o))
          : [updated, ...prev.data],
      };
    });
    setSelectedOrder((prev) => (prev && prev._id === updated._id ? { ...prev, ...updated } : prev));
  }, [qc]);

  const handleUpdateStatus = useCallback(
    async (order, newStatus) => {
      if (!order?._id || actionLoading) return;
      setActionLoading(true);
      try {
        const res = await orderService.updateStatus(order._id, newStatus);
        const updated = res?.data || { ...order, status: newStatus };
        applyLocalUpdate(updated);
        toast.success(
          `Commande ${order.orderNumber} → ${STATUS_CONFIG[newStatus]?.label || newStatus}`,
          { title: 'Statut mis à jour' }
        );
      } catch (err) {
        console.error('❌ Erreur mise à jour statut:', err);
        toast.error(err.message || 'Impossible de mettre à jour le statut');
      } finally {
        setActionLoading(false);
      }
    },
    [actionLoading, applyLocalUpdate, toast]
  );

  const handleCancel = useCallback(
    async (order) => {
      if (!order?._id || actionLoading) return;
      const reason = window.prompt('Raison de l\'annulation ?', '');
      if (reason === null) return; // annulé par l'utilisateur
      setActionLoading(true);
      try {
        const res = await orderService.cancelOrder(order._id, reason || 'Annulée par l\'opérateur');
        const updated = res?.data || { ...order, status: 'cancelled' };
        applyLocalUpdate(updated);
        toast.success(`Commande ${order.orderNumber} annulée`, { title: 'Commande annulée' });
      } catch (err) {
        console.error('❌ Erreur annulation:', err);
        toast.error(err.message || 'Impossible d\'annuler la commande');
      } finally {
        setActionLoading(false);
      }
    },
    [actionLoading, applyLocalUpdate, toast]
  );

  // ── Génération d'une facture depuis une commande ────────────────────────────
  const handleGenerateInvoice = useCallback(
    async (order) => {
      if (!order?._id || generatingInvoice) return;
      setGeneratingInvoice(true);
      try {
        const res = await invoiceService.createFromOrder(order._id);
        const draft = res?.data || res;
        if (!draft?._id) throw new Error('Facture non générée');
        toast.success(`Brouillon de facture généré pour ${order.orderNumber}`, {
          title: 'Facture',
        });
        // Ferme le détail commande et ouvre le brouillon dans la même modale facture
        setShowOrderDetails(false);
        setFactureDraft(draft);
        setShowFactureModal(true);
      } catch (err) {
        console.error('❌ Erreur génération facture:', err);
        toast.error(err.message || 'Impossible de générer la facture');
      } finally {
        setGeneratingInvoice(false);
      }
    },
    [generatingInvoice, toast]
  );

  const handleCloseFactureModal = useCallback(() => {
    setShowFactureModal(false);
    setFactureDraft(null);
  }, []);

  // ── Création manuelle d'une commande ────────────────────────────────────────
  const handleOrderCreated = useCallback(
    (created) => {
      setShowCreateModal(false);
      if (created && created._id) applyLocalUpdate(created);
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
    [applyLocalUpdate, qc]
  );

  // ── États chargement / erreur ───────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-green mx-auto mb-4" />
          <p className="text-label">Chargement des commandes...</p>
        </div>
      </div>
    );
  }

  if (isError && !ordersData) {
    return (
      <div className="card p-6" style={{ borderColor: 'var(--c-error)' }}>
        <div className="flex items-center gap-3 mb-4">
          <AlertCircle className="w-6 h-6" style={{ color: 'var(--c-error)' }} />
          <h3 className="font-semibold" style={{ color: 'var(--c-error)' }}>Erreur</h3>
        </div>
        <p className="text-label mb-4">{error?.message}</p>
        <button onClick={() => qc.invalidateQueries({ queryKey: ['orders'] })} className="btn-primary">
          <RefreshCw size={14} />
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* ── En-tête ──────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <p className="text-xs2 text-label uppercase tracking-widest mb-1">VENTES</p>
          <h1 className="text-ink font-semibold text-xl flex items-center gap-2">
            <ShoppingCart className="text-accent-green" size={20} />
            Commandes
          </h1>
        </div>
        <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
          <Plus size={14} />
          Nouvelle commande
        </button>
      </div>

      {/* ── Métriques ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { icon: Package, label: 'Total Commandes', value: totalOrders },
          { icon: DollarSign, label: 'Revenu Total', value: formatMoney(totalRevenue, primaryCurrency) },
          { icon: CheckCircle2, label: 'Taux Complétion', value: `${completionRate}%` },
          { icon: Clock, label: 'En Attente', value: pendingOrders },
        ].map((metric, i) => {
          const MetricIcon = metric.icon;
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              className="card-metric p-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)' }}>
                  <MetricIcon size={18} className="text-accent-green" />
                </div>
                <div className="min-w-0">
                  <p className="text-label truncate">{metric.label}</p>
                  <p className="font-metric text-xl text-ink font-bold truncate">{metric.value}</p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* ── Recherche + filtres ────────────────────────────────────────────── */}
      <div className="card p-4 mb-6 space-y-3">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-label" size={16} />
            <input
              type="text"
              placeholder="Rechercher par N° ou nom client..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full text-ink text-sm pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-green/50 transition-all"
              style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
            />
          </div>
          <div className="flex gap-2 items-center">
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="text-ink text-sm px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-green/50 cursor-pointer"
              style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
            >
              {DATE_FILTERS.map((d) => (
                <option key={d.id} value={d.id}>{d.label}</option>
              ))}
            </select>
            <button
              onClick={() => qc.invalidateQueries({ queryKey: ['orders'] })}
              className="p-2 rounded-lg transition-all hover:bg-[var(--c-hover)]"
              title="Rafraîchir"
            >
              <RefreshCw className={`text-label ${isFetching ? 'animate-spin' : ''}`} size={16} />
            </button>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setFilterStatus(filter.id)}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                filterStatus === filter.id
                  ? 'bg-accent-green text-black'
                  : 'text-text-gray hover:text-ink'
              }`}
              style={filterStatus !== filter.id ? { background: 'var(--c-hover)' } : {}}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-semibold text-label">
          {filteredOrders.length} commande{filteredOrders.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* ── Table ──────────────────────────────────────────────────────────── */}
      {filteredOrders.length === 0 ? (
        <div className="card p-8 sm:p-12 text-center">
          <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-label" />
          <h3 className="text-xl font-semibold text-ink mb-2">
            {searchQuery || filterStatus !== 'all' || dateFilter !== 'all'
              ? 'Aucun résultat'
              : 'Aucune commande'}
          </h3>
          <p className="text-label">
            {searchQuery || filterStatus !== 'all' || dateFilter !== 'all'
              ? "Essayez d'ajuster vos filtres"
              : 'Les commandes apparaîtront ici en temps réel'}
          </p>
        </div>
      ) : (
        <>
        {/* Tableau — desktop / tablette */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="hidden md:block card overflow-x-auto"
        >
          <table className="w-full min-w-[800px]">
            <thead style={{ background: 'var(--c-primary-sidebar)' }}>
              <tr>
                {['N° Commande', 'Client', 'Produits', 'Total', 'Statut', 'Date', ''].map((h, i) => (
                  <th key={i} className={`p-4 text-xs font-semibold text-label uppercase tracking-wider ${i === 6 ? 'text-right' : 'text-left'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => {
                const items = getItems(order);
                const firstItem = items[0];
                const statusCfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
                return (
                  <tr
                    key={order._id}
                    onClick={() => handleViewOrderDetails(order)}
                    className="cursor-pointer transition-colors hover:bg-[var(--c-hover)]"
                    style={{ borderTop: '0.5px solid var(--c-border)' }}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <span className="text-accent-green font-medium text-sm">{order.orderNumber}</span>
                        {isWhatsAppAI(order) && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded text-[10px] font-semibold whitespace-nowrap">
                            <Bot size={10} /> WhatsApp
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <p className="text-ink font-medium text-sm">{order.clientName || 'Client'}</p>
                      {cleanValue(order.clientPhone) && (
                        <p className="text-label text-xs">{cleanValue(order.clientPhone)}</p>
                      )}
                    </td>
                    <td className="p-4">
                      <p className="text-ink text-sm">
                        {items.length > 0 ? `${items.length} article${items.length > 1 ? 's' : ''}` : '—'}
                      </p>
                      {firstItem && (
                        <p className="text-label text-xs truncate max-w-[160px]">
                          {firstItem.productName}{items.length > 1 ? ` +${items.length - 1}` : ''}
                        </p>
                      )}
                    </td>
                    <td className="p-4 text-ink font-semibold text-sm">
                      {formatMoney(order.total, order.currency)}
                    </td>
                    <td className="p-4">
                      <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${statusCfg.cls}`}>
                        {statusCfg.label}
                      </span>
                    </td>
                    <td className="p-4 text-label text-sm">{formatDate(order.createdAt)}</td>
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end">
                        <motion.button
                          onClick={() => handleViewOrderDetails(order)}
                          className="p-2 hover:bg-accent-green/15 rounded-lg text-accent-green transition-all"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <Eye size={16} />
                        </motion.button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </motion.div>

        {/* Cartes — mobile */}
        <div className="md:hidden space-y-3">
          {filteredOrders.map((order, i) => {
            const items = getItems(order);
            const firstItem = items[0];
            const statusCfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
            return (
              <motion.div
                key={order._id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                onClick={() => handleViewOrderDetails(order)}
                className="card p-4 cursor-pointer relative overflow-hidden"
              >
                <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-accent-green" />
                <div className="flex items-start justify-between gap-3 mb-3 pl-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-accent-green font-medium text-sm">{order.orderNumber}</span>
                      {isWhatsAppAI(order) && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded text-[10px] font-semibold whitespace-nowrap">
                          <Bot size={10} /> WhatsApp
                        </span>
                      )}
                    </div>
                    <p className="text-ink font-medium truncate mt-1 text-sm">{order.clientName || 'Client'}</p>
                    {cleanValue(order.clientPhone) && (
                      <p className="text-label text-xs truncate">{cleanValue(order.clientPhone)}</p>
                    )}
                  </div>
                  <span className={`px-2.5 py-1 rounded-lg text-xs flex-shrink-0 font-semibold ${statusCfg.cls}`}>
                    {statusCfg.label}
                  </span>
                </div>
                <div className="flex items-end justify-between gap-3 pl-3">
                  <div className="min-w-0">
                    <p className="text-ink text-sm">
                      {items.length > 0 ? `${items.length} article${items.length > 1 ? 's' : ''}` : '—'}
                    </p>
                    {firstItem && (
                      <p className="text-label text-xs truncate max-w-[180px]">
                        {firstItem.productName}{items.length > 1 ? ` +${items.length - 1}` : ''}
                      </p>
                    )}
                    <p className="text-label text-xs mt-1">{formatDate(order.createdAt)}</p>
                  </div>
                  <p className="text-ink font-semibold whitespace-nowrap flex-shrink-0 text-sm">
                    {formatMoney(order.total, order.currency)}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
        </>
      )}

      {/* ── Modal détail ───────────────────────────────────────────────────── */}
      <AnimatePresence>
        {showOrderDetails && selectedOrder && (
          <OrderDetailModal
            order={selectedOrder}
            onClose={handleCloseOrderDetails}
            onUpdateStatus={handleUpdateStatus}
            onCancel={handleCancel}
            onGenerateInvoice={handleGenerateInvoice}
            generatingInvoice={generatingInvoice}
            actionLoading={actionLoading}
            navigate={navigate}
          />
        )}
      </AnimatePresence>

      {/* ── Modal facture (brouillon généré depuis une commande) ───────────── */}
      <AnimatePresence>
        {showFactureModal && (
          <FactureModal
            invoice={factureDraft}
            branding={loadLocalBranding()}
            onClose={handleCloseFactureModal}
            onSaved={handleCloseFactureModal}
          />
        )}
      </AnimatePresence>

      {/* ── Modal création d'une commande ──────────────────────────────────── */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateOrderModal
            onClose={() => setShowCreateModal(false)}
            onCreated={handleOrderCreated}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Modal détail (composant séparé pour la lisibilité) ───────────────────────

function OrderDetailModal({ order, onClose, onUpdateStatus, onCancel, onGenerateInvoice, generatingInvoice, actionLoading, navigate }) {
  const items = getItems(order);
  const statusCfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
  const paymentCfg = PAYMENT_CONFIG[order.paymentStatus] || PAYMENT_CONFIG.pending;
  const currency = order.currency || 'EUR';

  const email = cleanValue(order.clientEmail);
  const phone = cleanValue(order.clientPhone);
  const address = cleanValue(order.shippingAddress);
  const notes = cleanValue(order.notes);
  const conversationId = order.conversationId || order.metadata?.conversationId;

  // Visibilité des boutons d'action selon le statut
  const canConfirm = order.status === 'pending';
  const canDeliver = ['confirmed', 'processing', 'shipped'].includes(order.status);
  const canCancel = !['delivered', 'cancelled', 'refunded'].includes(order.status);
  const canInvoice = !['cancelled', 'refunded'].includes(order.status);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="glass rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
        style={{ border: '0.5px solid var(--c-border)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 sm:p-6" style={{ borderBottom: '0.5px solid var(--c-border)', background: 'var(--c-primary-sidebar)' }}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(var(--c-accent-green-rgb), 0.15)' }}>
                <Package className="text-accent-green" size={20} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg font-semibold text-ink truncate">Commande {order.orderNumber}</h2>
                  {isWhatsAppAI(order) && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded-md text-xs font-semibold">
                      <Bot size={12} /> WhatsApp IA
                    </span>
                  )}
                </div>
                <p className="text-label text-sm">{formatDate(order.createdAt, true)}</p>
              </div>
            </div>
            <motion.button
              onClick={onClose}
              className="p-2 rounded-xl text-label hover:text-ink transition-all hover:bg-[var(--c-hover)] flex-shrink-0"
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
            >
              <X size={20} />
            </motion.button>
          </div>
        </div>

        {/* Contenu */}
        <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Colonne principale */}
            <div className="lg:col-span-2 space-y-4">
              {/* Statut + paiement */}
              <div className="card p-4 sm:p-5">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                  <h3 className="text-base font-semibold text-ink flex items-center gap-2">
                    <Tag className="text-accent-green" size={18} />
                    Statut
                  </h3>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-3 py-1.5 rounded-full text-sm font-semibold ${statusCfg.cls}`}>
                      {statusCfg.label}
                    </span>
                    <span className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1 ${paymentCfg.cls}`}>
                      <CreditCard size={12} />
                      {paymentCfg.label}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <DollarSign className="text-green-400" size={20} />
                  <div>
                    <p className="text-label text-xs">Montant total</p>
                    <p className="text-2xl font-bold text-ink">{formatMoney(order.total, currency)}</p>
                  </div>
                </div>
              </div>

              {/* Infos client */}
              <div className="card p-4 sm:p-5">
                <h3 className="text-base font-semibold text-ink mb-4 flex items-center gap-2">
                  <User className="text-accent-green" size={18} />
                  Informations client
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)' }}>
                      <User className="text-accent-green" size={16} />
                    </div>
                    <p className="text-ink font-semibold">{order.clientName || 'Client'}</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {phone && (
                      <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'var(--c-primary-dark)' }}>
                        <Phone className="text-label flex-shrink-0" size={16} />
                        <div className="min-w-0">
                          <p className="text-label text-xs">Téléphone</p>
                          <p className="text-ink text-sm truncate">{phone}</p>
                        </div>
                      </div>
                    )}
                    {email && (
                      <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'var(--c-primary-dark)' }}>
                        <Mail className="text-label flex-shrink-0" size={16} />
                        <div className="min-w-0">
                          <p className="text-label text-xs">Email</p>
                          <p className="text-ink text-sm truncate">{email}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {address && (
                    <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'var(--c-primary-dark)' }}>
                      <MapPin className="text-label flex-shrink-0" size={16} />
                      <div className="min-w-0">
                        <p className="text-label text-xs">Adresse de livraison</p>
                        <p className="text-ink text-sm">{address}</p>
                      </div>
                    </div>
                  )}

                  {!phone && !email && !address && (
                    <p className="text-label text-sm italic">Aucune coordonnée détaillée fournie.</p>
                  )}
                </div>
              </div>

              {/* Produits commandés */}
              <div className="card p-4 sm:p-5">
                <h3 className="text-base font-semibold text-ink mb-4 flex items-center gap-2">
                  <Box className="text-accent-green" size={18} />
                  Produits commandés
                </h3>

                {items.length === 0 ? (
                  <p className="text-label text-sm italic">Aucun produit listé.</p>
                ) : (
                  <div className="space-y-3">
                    {items.map((item, i) => (
                      <div
                        key={item.productId || i}
                        className="flex items-center gap-4 p-4 rounded-xl"
                        style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                      >
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)' }}>
                          <Package style={{ color: 'rgb(var(--c-accent-green-rgb))' }} size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-ink font-semibold truncate">{item.productName || 'Produit'}</p>
                          <p className="text-label text-sm">
                            {item.quantity} × {formatMoney(item.price, currency)}
                          </p>
                        </div>
                        <p className="text-ink font-bold whitespace-nowrap">
                          {formatMoney(item.total ?? item.price * item.quantity, currency)}
                        </p>
                      </div>
                    ))}

                    {/* Récapitulatif */}
                    <div className="pt-3 mt-3 space-y-1.5 text-sm" style={{ borderTop: '0.5px solid var(--c-border)' }}>
                      {order.subtotal != null && (
                        <div className="flex justify-between text-label">
                          <span>Sous-total</span>
                          <span>{formatMoney(order.subtotal, currency)}</span>
                        </div>
                      )}
                      {order.tax ? (
                        <div className="flex justify-between text-label">
                          <span>TVA</span>
                          <span>{formatMoney(order.tax, currency)}</span>
                        </div>
                      ) : null}
                      {order.shipping ? (
                        <div className="flex justify-between text-label">
                          <span>Livraison</span>
                          <span>{formatMoney(order.shipping, currency)}</span>
                        </div>
                      ) : null}
                      <div className="flex justify-between text-ink font-bold text-base pt-1">
                        <span>Total</span>
                        <span>{formatMoney(order.total, currency)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Notes */}
              {notes && (
                <div className="card p-4 sm:p-5">
                  <h3 className="text-base font-semibold text-ink mb-3 flex items-center gap-2">
                    <MessageSquare className="text-accent-green" size={18} />
                    Note
                  </h3>
                  <p className="text-label text-sm whitespace-pre-wrap">{notes}</p>
                </div>
              )}
            </div>

            {/* Colonne actions */}
            <div className="space-y-4">
              <div className="card p-4 sm:p-5">
                <h3 className="text-base font-semibold text-ink mb-4 flex items-center gap-2">
                  <CheckCircle2 className="text-accent-green" size={18} />
                  Actions
                </h3>
                <div className="space-y-2.5">
                  {canConfirm && (
                    <motion.button
                      onClick={() => onUpdateStatus(order, 'confirmed')}
                      disabled={actionLoading}
                      className="w-full p-2.5 bg-blue-500/15 border border-blue-500/30 text-blue-400 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-blue-500/25 transition-all disabled:opacity-50 text-sm"
                      whileHover={{ scale: actionLoading ? 1 : 1.02 }}
                      whileTap={{ scale: actionLoading ? 1 : 0.98 }}
                    >
                      <Check size={16} />
                      Confirmer la commande
                    </motion.button>
                  )}

                  {canDeliver && (
                    <motion.button
                      onClick={() => onUpdateStatus(order, 'delivered')}
                      disabled={actionLoading}
                      className="w-full p-2.5 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-50 text-sm text-black"
                      style={{ background: 'rgb(var(--c-accent-green-rgb))' }}
                      whileHover={{ scale: actionLoading ? 1 : 1.02 }}
                      whileTap={{ scale: actionLoading ? 1 : 0.98 }}
                    >
                      <Truck size={16} />
                      Marquer comme livrée
                    </motion.button>
                  )}

                  {canInvoice && (
                    <motion.button
                      onClick={() => onGenerateInvoice(order)}
                      disabled={actionLoading || generatingInvoice}
                      className="w-full p-2.5 bg-purple-500/15 border border-purple-500/30 text-purple-400 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-purple-500/25 transition-all disabled:opacity-50 text-sm"
                      whileHover={{ scale: actionLoading || generatingInvoice ? 1 : 1.02 }}
                      whileTap={{ scale: actionLoading || generatingInvoice ? 1 : 0.98 }}
                    >
                      {generatingInvoice ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-400" />
                      ) : (
                        <FileText size={18} />
                      )}
                      {generatingInvoice ? 'Génération…' : 'Générer une facture'}
                    </motion.button>
                  )}

                  {canCancel && (
                    <motion.button
                      onClick={() => onCancel(order)}
                      disabled={actionLoading}
                      className="w-full p-3 bg-red-500/15 border border-red-500/40 text-red-400 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-red-500/25 transition-all disabled:opacity-50"
                      whileHover={{ scale: actionLoading ? 1 : 1.02 }}
                      whileTap={{ scale: actionLoading ? 1 : 0.98 }}
                    >
                      <Ban size={18} />
                      Annuler la commande
                    </motion.button>
                  )}

                  {!canConfirm && !canDeliver && !canCancel && !canInvoice && (
                    <p className="text-label text-sm text-center italic py-2">
                      Aucune action disponible pour ce statut.
                    </p>
                  )}

                  {actionLoading && (
                    <div className="flex items-center justify-center gap-2 text-label text-sm pt-1">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent-green" />
                      Traitement…
                    </div>
                  )}
                </div>
              </div>

              {/* Lien conversation WhatsApp */}
              {conversationId && (
                <div className="card p-4 sm:p-5">
                  <h3 className="text-base font-semibold text-ink mb-3 flex items-center gap-2">
                    <Bot className="text-accent-green" size={18} />
                    Origine WhatsApp
                  </h3>
                  <p className="text-label text-xs mb-3">
                    Cette commande provient d'une conversation WhatsApp gérée par l'IA.
                    {order.metadata?.intentConfidence != null && (
                      <> Confiance d'intention&nbsp;:&nbsp;
                        <span className="text-accent-green font-semibold">
                          {Math.round(order.metadata.intentConfidence * 100)}%
                        </span>
                      </>
                    )}
                  </p>
                  <button
                    onClick={() => navigate(`/conversations?id=${conversationId}`)}
                    className="w-full p-2.5 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all text-sm"
                    style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)', color: 'rgb(var(--c-accent-green-rgb))', border: '1px solid rgba(var(--c-accent-green-rgb), 0.3)' }}
                  >
                    <MessageSquare size={16} />
                    Voir la conversation
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Modal création d'une commande ────────────────────────────────────────────

const inputCls =
  'w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent-green/50 transition-all text-ink';

function CreateOrderModal({ onClose, onCreated }) {
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);

  // Client
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');

  // Lignes de commande : [{ productId, quantity }]
  const [items, setItems] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [quantity, setQuantity] = useState(1);

  // Adresse de livraison (tous les champs sont requis côté backend)
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [stateField, setStateField] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('');

  const [notes, setNotes] = useState('');

  // Produits depuis le cache React Query (partagé avec ProduitsConnected)
  const { data: productsData, isLoading: loadingProducts } = useQuery({
    queryKey: ['products'],
    queryFn: () => productService.getProducts(),
  });

  const products = (productsData?.success && Array.isArray(productsData.data))
    ? productsData.data
    : [];

  const productMap = useMemo(() => {
    const m = new Map();
    products.forEach((p) => m.set(p._id, p));
    return m;
  }, [products]);

  const currency = useMemo(() => {
    const firstItem = items[0] && productMap.get(items[0].productId);
    return firstItem?.currency || products[0]?.currency || 'EUR';
  }, [items, productMap, products]);

  const subtotal = useMemo(
    () =>
      items.reduce((sum, it) => {
        const p = productMap.get(it.productId);
        return sum + (p ? p.price * it.quantity : 0);
      }, 0),
    [items, productMap]
  );

  const addItem = () => {
    const p = productMap.get(selectedProductId);
    if (!p) return;
    const qty = Math.max(1, parseInt(quantity, 10) || 1);
    setItems((prev) => {
      const existing = prev.find((it) => it.productId === selectedProductId);
      if (existing) {
        return prev.map((it) =>
          it.productId === selectedProductId ? { ...it, quantity: it.quantity + qty } : it
        );
      }
      return [...prev, { productId: selectedProductId, quantity: qty }];
    });
    setSelectedProductId('');
    setQuantity(1);
  };

  const removeItem = (productId) =>
    setItems((prev) => prev.filter((it) => it.productId !== productId));

  const handleSubmit = async () => {
    if (submitting) return;

    if (!clientName.trim() || !clientEmail.trim()) {
      toast.error('Le nom et l\'email du client sont requis');
      return;
    }
    if (items.length === 0) {
      toast.error('Ajoutez au moins un produit à la commande');
      return;
    }
    if (!street.trim() || !city.trim() || !stateField.trim() || !postalCode.trim() || !country.trim()) {
      toast.error('Veuillez renseigner l\'adresse de livraison complète');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        // Pas de fiche client pour une commande manuelle : on génère un identifiant.
        clientId: `manual-${Date.now()}`,
        clientName: clientName.trim(),
        clientEmail: clientEmail.trim(),
        clientPhone: clientPhone.trim() || undefined,
        items: items.map((it) => ({ productId: it.productId, quantity: it.quantity })),
        currency,
        shippingAddress: {
          street: street.trim(),
          city: city.trim(),
          state: stateField.trim(),
          postalCode: postalCode.trim(),
          country: country.trim(),
        },
        notes: notes.trim() || undefined,
        metadata: { source: 'manual' },
      };

      const res = await orderService.createOrder(payload);
      const created = res?.data || res;
      toast.success(`Commande ${created?.orderNumber || ''} créée`, { title: 'Commande créée' });
      onCreated(created);
    } catch (err) {
      console.error('❌ Erreur création commande:', err);
      toast.error(err.message || 'Impossible de créer la commande');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="glass rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
        style={{ border: '0.5px solid var(--c-border)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 sm:p-6 flex items-center justify-between gap-3" style={{ background: 'var(--c-primary-sidebar)', borderBottom: '0.5px solid var(--c-border)' }}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)' }}>
              <ShoppingCart className="text-accent-green" size={20} />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-semibold text-ink truncate">Nouvelle commande</h2>
              <p className="text-label text-sm">Créez une commande manuellement</p>
            </div>
          </div>
          <motion.button
            onClick={onClose}
            className="p-2 rounded-xl transition-all flex-shrink-0 text-label hover:text-ink"
            style={{ background: 'transparent' }}
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
          >
            <X size={20} />
          </motion.button>
        </div>

        {/* Contenu */}
        <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-220px)] space-y-6">
          {/* Client */}
          <section className="card p-4">
            <h3 className="text-base font-semibold text-ink mb-3 flex items-center gap-2">
              <User className="text-accent-green" size={18} />
              Informations client
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-label text-xs mb-1">Nom du client *</label>
                <input className={inputCls} style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }} value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Jean Dupont" />
              </div>
              <div>
                <label className="block text-label text-xs mb-1">Email *</label>
                <input className={inputCls} style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }} type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} placeholder="client@email.com" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-label text-xs mb-1">Téléphone</label>
                <input className={inputCls} style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }} value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} placeholder="+33 6 12 34 56 78" />
              </div>
            </div>
          </section>

          {/* Produits */}
          <section className="card p-4">
            <h3 className="text-base font-semibold text-ink mb-3 flex items-center gap-2">
              <Box className="text-accent-green" size={18} />
              Produits
            </h3>

            <div className="flex flex-col sm:flex-row gap-2 mb-3">
              <select
                className={`${inputCls} flex-1`}
                style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                value={selectedProductId}
                onChange={(e) => setSelectedProductId(e.target.value)}
                disabled={loadingProducts}
              >
                <option value="">
                  {loadingProducts ? 'Chargement des produits…' : 'Sélectionner un produit…'}
                </option>
                {products.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.name} — {formatMoney(p.price, p.currency || 'EUR')}
                    {p.stock != null ? ` (stock: ${p.stock})` : ''}
                  </option>
                ))}
              </select>
              <input
                type="number"
                min="1"
                className={`${inputCls} sm:w-24`}
                style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
              <button
                type="button"
                onClick={addItem}
                disabled={!selectedProductId}
                className="px-4 py-2 bg-accent-green text-black rounded-lg font-semibold flex items-center justify-center gap-1 hover:bg-accent-green/80 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus size={16} />
                Ajouter
              </button>
            </div>

            {items.length === 0 ? (
              <p className="text-label text-sm italic">Aucun produit ajouté pour le moment.</p>
            ) : (
              <div className="space-y-2">
                {items.map((it) => {
                  const p = productMap.get(it.productId);
                  if (!p) return null;
                  return (
                    <div key={it.productId} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}>
                      <div className="flex-1 min-w-0">
                        <p className="text-ink font-medium truncate">{p.name}</p>
                        <p className="text-label text-xs">{it.quantity} × {formatMoney(p.price, currency)}</p>
                      </div>
                      <p className="text-ink font-bold whitespace-nowrap">{formatMoney(p.price * it.quantity, currency)}</p>
                      <button
                        type="button"
                        onClick={() => removeItem(it.productId)}
                        className="p-2 hover:bg-red-500/20 rounded-lg text-red-400 transition-all"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  );
                })}
                <div className="flex justify-between text-ink font-bold pt-2" style={{ borderTop: '0.5px solid var(--c-border)' }}>
                  <span>Sous-total</span>
                  <span>{formatMoney(subtotal, currency)}</span>
                </div>
                <p className="text-label text-xs">Le total final (TVA, livraison) est calculé par le serveur.</p>
              </div>
            )}
          </section>

          {/* Adresse de livraison */}
          <section className="card p-4">
            <h3 className="text-base font-semibold text-ink mb-3 flex items-center gap-2">
              <MapPin className="text-accent-green" size={18} />
              Adresse de livraison
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="block text-label text-xs mb-1">Rue *</label>
                <input className={inputCls} style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }} value={street} onChange={(e) => setStreet(e.target.value)} placeholder="12 rue de la Paix" />
              </div>
              <div>
                <label className="block text-label text-xs mb-1">Ville *</label>
                <input className={inputCls} style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }} value={city} onChange={(e) => setCity(e.target.value)} placeholder="Paris" />
              </div>
              <div>
                <label className="block text-label text-xs mb-1">Région / État *</label>
                <input className={inputCls} style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }} value={stateField} onChange={(e) => setStateField(e.target.value)} placeholder="Île-de-France" />
              </div>
              <div>
                <label className="block text-label text-xs mb-1">Code postal *</label>
                <input className={inputCls} style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }} value={postalCode} onChange={(e) => setPostalCode(e.target.value)} placeholder="75002" />
              </div>
              <div>
                <label className="block text-label text-xs mb-1">Pays *</label>
                <input className={inputCls} style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }} value={country} onChange={(e) => setCountry(e.target.value)} placeholder="France" />
              </div>
            </div>
          </section>

          {/* Notes */}
          <section className="card p-4">
            <h3 className="text-base font-semibold text-ink mb-3 flex items-center gap-2">
              <MessageSquare className="text-accent-green" size={18} />
              Note (optionnel)
            </h3>
            <textarea
              className={`${inputCls} resize-none`}
              style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
              rows="2"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Instructions particulières…"
            />
          </section>
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-6 flex items-center justify-end gap-3" style={{ borderTop: '0.5px solid var(--c-border)' }}>
          <button
            onClick={onClose}
            disabled={submitting}
            className="btn-ghost disabled:opacity-50"
          >
            Annuler
          </button>
          <motion.button
            onClick={handleSubmit}
            disabled={submitting}
            whileHover={{ scale: submitting ? 1 : 1.02 }}
            whileTap={{ scale: submitting ? 1 : 0.98 }}
            className="btn-primary disabled:opacity-50"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black" />
                Création…
              </>
            ) : (
              <>
                <Check size={16} />
                Créer la commande
              </>
            )}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}
